import { FarmerSalesIcon } from 'core/components/Icons'
import { Badge } from 'core/components/ui/Badge'
import { Column, Table } from 'core/components/ui/Table'
import { FarmerSaleRecord } from '../../domain/entities/FarmerSale'

interface FarmerDirectoryProps {
  records: FarmerSaleRecord[]
  loading: boolean
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  totalItems: number
  itemsPerPage: number
  onSelect: (record: FarmerSaleRecord) => void
}

const formatCurrency = (val: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(val)
}

const formatDate = (val: string) => {
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const paymentModeTones: Record<string, 'primary' | 'warning' | 'secondary'> = {
  upi: 'primary',
  credit: 'warning',
}

const getPaymentModeTone = (mode: string): 'primary' | 'warning' | 'secondary' => {
  return paymentModeTones[mode] || 'secondary'
}

export function FarmerDirectory({
  records,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  onSelect,
}: FarmerDirectoryProps) {
  const columns: Column<FarmerSaleRecord>[] = [
    {
      key: 'fpo',
      label: 'FPO',
      render: (row) =>
        row.fpo ? (
          <div className="flex flex-col">
            <span className="font-semibold text-text-primary text-sm truncate max-w-[140px]" title={row.fpo.name}>
              {row.fpo.name}
            </span>
            <span className="text-xs text-text-secondary font-mono">{row.fpo.id}</span>
          </div>
        ) : (
          <span className="text-text-secondary font-mono text-sm">-</span>
        ),
    },
    {
      key: 'retailer',
      label: 'Retailer',
      render: (row) =>
        row.retailer ? (
          <div className="flex flex-col">
            <span className="font-semibold text-text-primary text-sm truncate max-w-[140px]" title={row.retailer.name}>
              {row.retailer.name}
            </span>
            <span className="text-xs text-text-secondary font-mono">
              {row.retailer.id}
              {row.retailer.phone ? ` • ${row.retailer.phone}` : ''}
            </span>
          </div>
        ) : (
          <span className="text-text-secondary font-mono text-sm">-</span>
        ),
    },
    {
      key: 'vlcc',
      label: 'VLCC',
      render: (row) =>
        row.vlcc ? (
          <div className="flex flex-col">
            <span className="font-semibold text-text-primary text-sm truncate max-w-[140px]" title={row.vlcc.name}>
              {row.vlcc.name}
            </span>
            <span className="text-xs text-text-secondary font-mono">
              {row.vlcc.id}
              {row.vlcc.phone ? ` • ${row.vlcc.phone}` : ''}
            </span>
          </div>
        ) : (
          <span className="text-text-secondary font-mono text-sm">-</span>
        ),
    },
    {
      key: 'farmer',
      label: 'Farmer',
      render: (row) =>
        row.farmer ? (
          <div className="flex flex-col">
            <span className="font-semibold text-text-primary text-sm truncate max-w-[140px]" title={row.farmer.name}>
              {row.farmer.name}
            </span>
            <span className="text-xs text-text-secondary font-mono">
              {row.farmer.id}
              {row.farmer.phone ? ` • ${row.farmer.phone}` : ''}
            </span>
          </div>
        ) : (
          <span className="text-text-secondary font-mono text-sm">-</span>
        ),
    },
    {
      key: 'units',
      label: 'Units Purchased',
      render: (row) => <span className="font-semibold text-text-primary">{row.units} Units</span>,
    },
    {
      key: 'amount',
      label: 'Financials',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-bold text-text-primary text-sm">Total: {formatCurrency(row.amount)}</span>
          <span className="text-xs text-text-secondary">Paid: {formatCurrency(row.amountPaid)}</span>
          {row.amountOutstanding > 0 && (
            <span className="text-xs font-semibold text-danger">
              Outstanding: {formatCurrency(row.amountOutstanding)}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'paymentMode',
      label: 'Payment Mode',
      render: (row) => {
        const tone = getPaymentModeTone(row.paymentMode)
        return (
          <Badge tone={tone} variant="subtle" className="uppercase">
            {row.paymentMode}
          </Badge>
        )
      },
    },
    {
      key: 'createdAt',
      label: 'Sales Date',
      render: (row) => <span className="text-xs text-text-secondary">{formatDate(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-center',
      render: (row) => (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => onSelect(row)}
            className="p-1.5 rounded-btn hover:bg-bg text-text-secondary hover:text-text-primary transition-colors text-xs font-semibold flex items-center gap-1 border border-border/50 hover:border-border dark:border-[rgba(255,255,255,0.14)]"
          >
            <svg className="h-3.5 w-3.5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            Inspect Log
          </button>
        </div>
      ),
    },
  ]

  return (
    <Table
      columns={columns}
      data={records}
      loading={loading}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
        totalItems,
        itemsPerPage,
      }}
      emptyMessage="No farmer sales transaction records."
      emptyIcon={<FarmerSalesIcon size={20} className="opacity-60" />}
    />
  )
}
